import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/auth.service';
import permissaoUsuarioService from '../services/permissaoUsuario.service';
import { useAuth } from './AuthContext';

// ════════════════════════════════════════════════════════════════════════════
// PermissoesContext.jsx — r87 (Rodada C)
// ────────────────────────────────────────────────────────────────────────────
// Gerencia permissões do usuário logado:
//   - Carrega via permissaoUsuarioService quando o usuário loga
//   - Detecta mudança de versão pelo header x-permissoes-versao (response axios)
//   - Recarrega quando versão muda
//   - Expõe helper `pode(rotina, acao)` via hook usePermissoes()
//
// Posicionamento na árvore React:
//   <AuthProvider>
//     <PermissoesProvider>
//       <App />
//     </PermissoesProvider>
//   </AuthProvider>
// ════════════════════════════════════════════════════════════════════════════

const PermissoesContext = createContext(null);

// Chave no localStorage pra cache persistente entre sessões F5
const LS_KEY = 'permissoesResolvidas';

function lerCache() {
  try {
    const s = localStorage.getItem(LS_KEY);
    return s ? JSON.parse(s) : null;
  } catch { return null; }
}

function gravarCache(dados) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(dados)); } catch {}
}

function limparCache() {
  try { localStorage.removeItem(LS_KEY); } catch {}
}

export function PermissoesProvider({ children }) {
  const { usuario } = useAuth();
  const [permissoes, setPermissoes] = useState(() => lerCache());
  const [carregando, setCarregando] = useState(false);

  // Ref pra evitar carregar várias vezes em paralelo
  const carregandoRef = useRef(false);

  // ── Carrega/recarrega permissões do servidor ─────────────────────────────
  const recarregar = useCallback(async () => {
    if (!usuario?.id || carregandoRef.current) return;
    carregandoRef.current = true;
    setCarregando(true);
    try {
      const dados = await permissaoUsuarioService.resolverPermissoes(usuario.id);
      setPermissoes(dados);
      gravarCache(dados);
    } catch (err) {
      console.warn('Falha ao carregar permissões:', err.message);
      // Mantém cache local se houver; se não houver, fica null e telas vão
      // se comportar como "sem permissão" (seguro).
    } finally {
      carregandoRef.current = false;
      setCarregando(false);
    }
  }, [usuario?.id]);

  // ── Carrega no mount + quando usuário muda (login/logout) ────────────────
  useEffect(() => {
    if (!usuario?.id) {
      // Logout — limpa cache
      setPermissoes(null);
      limparCache();
      return;
    }
    // Se o cache no localStorage é de OUTRO usuário, invalida
    const cache = lerCache();
    if (cache?.grupo_id) {
      // ok, tenta usar (se for de outro usuário, recarrega abaixo de qualquer jeito)
    }
    recarregar();
  }, [usuario?.id, recarregar]);

  // ── Interceptor de response: detecta mudança de versão e recarrega ───────
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => {
        try {
          const versaoServer = parseInt(response.headers['x-permissoes-versao'] || '0', 10);
          const versaoLocal  = permissoes?.versao || 0;
          if (versaoServer > 0 && versaoServer !== versaoLocal) {
            // Versão mudou — recarrega em background (sem await pra não atrasar a response)
            recarregar();
          }
        } catch { /* ignora */ }
        return response;
      },
      (error) => Promise.reject(error)
    );
    return () => api.interceptors.response.eject(interceptor);
  }, [permissoes?.versao, recarregar]);

  // ── Helper principal: pode(rotina, acao) ─────────────────────────────────
  const pode = useCallback((rotina, acao = 'visualizar') => {
    // Enquanto carrega, libera (evita "flash" de menu vazio). Quando carregar
    // de verdade, o estado real entra em vigor. Se a request falhou, fica em
    // permissoes=null e retorna false (seguro).
    if (carregando) return true;
    if (!permissoes) return false;                  // não carregou e não está carregando → bloqueia
    if (permissoes.admin) return true;              // admin libera tudo
    if (permissoes.bootstrap) return true;          // modo bootstrap libera tudo

    const r = permissoes.rotinas?.[rotina];
    if (!r) return false;                           // rotina não configurada → bloqueia
    return !!r[acao]?.permitido;
  }, [permissoes, carregando]);

  // Variantes úteis
  const podeQualquer = useCallback((rotina) => {
    if (carregando) return true;
    if (!permissoes) return false;
    if (permissoes.admin || permissoes.bootstrap) return true;
    const r = permissoes.rotinas?.[rotina];
    if (!r) return false;
    return Object.values(r).some(a => a.permitido);
  }, [permissoes, carregando]);

  // Retorna o código do filtro aplicado a uma rotina (ou null)
  const filtroDaRotina = useCallback((rotina) => {
    if (!permissoes || permissoes.admin || permissoes.bootstrap) return null;
    const r = permissoes.rotinas?.[rotina];
    if (!r) return null;
    for (const acao of Object.values(r)) {
      if (acao.filtro) return acao.filtro;
    }
    return null;
  }, [permissoes]);

  const value = {
    permissoes,
    carregando,
    isAdmin: !!permissoes?.admin,
    isBootstrap: !!permissoes?.bootstrap,
    pode,
    podeQualquer,
    filtroDaRotina,
    recarregar,
  };

  return (
    <PermissoesContext.Provider value={value}>
      {children}
    </PermissoesContext.Provider>
  );
}

export function usePermissoes() {
  const ctx = useContext(PermissoesContext);
  if (!ctx) throw new Error('usePermissoes precisa estar dentro de <PermissoesProvider>');
  return ctx;
}
